import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksElement } from '../BlocksElement';
import '../card/card';
import '../text-link/text-link';
import styles from './header.styles';

/**
 * @slot skip-link - slot for custom skip link
 * @slot top - The header top
 * @slot default - The header default slot
 * @slot bottom - The header bottom
 * @csspart base - The component's base wrapper.
 * @csspart skip-link-card - The component's skip link card.
 * @csspart part-skip-link - The component's skip link.
 * @csspart skip-link-text-link - The component's skip link text..
 * @csspart top-wrapper - The component's top wrapper.
 * @csspart top-inner - The component's top inner.
 * @csspart part-top - The component's top slot.
 * @csspart middle-wrapper - The component's middle wrapper.
 * @csspart middle-container - The component's middle container.
 * @csspart middle-inner - The component's middle-inner wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart bottom-wrapper - The component's bottom wrapper.
 * @csspart part-bottom - The component's bottom slot.
 */
export class BlocksHeader extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * controls skip link visibility
   */
  @property({ type: Boolean })
  showSkipLink = false;

  /**
   * skip link text
   */
  @property({ type: String })
  skipLinkText = 'Skip to Main Content';

  /**
   * The target ID of the element to which the skip link will jump. Should correspond to the
   * ID of a target element on the page.
   */
  @property({ type: String })
  href: string | undefined;

  /**
   * Is active state
   * 1) Set to true when small screen menu is open
   */
  @state()
  private isActive: boolean | undefined;

  /**
   * tracks focus state of skip link
   */
  @state()
  private isSkipLinkActive = false;

  protected slots = new SlotController(this, 'top', 'bottom', 'skip-link', null);

  private getSkipLinkTemplate(): TemplateResult<1> {
    return html`
      <slot part="part-skip-link" name="skip-link"></slot>
      <cat-text-link
        part="skip-link-text-link"
        exportparts=${this.getExportParts('text-link')}
        .href="${this.href}"
        .isSkipLink="${true}"
        @focus="${() => {
          this.isSkipLinkActive = true;
        }}"
        @blur="${() => {
          this.isSkipLinkActive = false;
        }}"
        >${this.skipLinkText}</cat-text-link
      >
    `;
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-header', {
      'cat-is-active': this.isActive === true,
      'cat-skip-link-active': this.isSkipLinkActive === true,
      'cat-c-header--has-top-slot': this.slots.hasSlotted('top'),
      'cat-c-header--has-bottom-slot': this.slots.hasSlotted('bottom'),
      'cat-c-header--has-skip-link-slot': this.slots.hasSlotted('skip-link')
    });

    return html`
      <header part="base" class="${componentClassName}">
        ${this.showSkipLink
          ? html` <cat-card part="skip-link-card" exportparts=${this.getExportParts('card')} class="cat-c-header__skip-link">
              ${this.getSkipLinkTemplate()}
            </cat-card>`
          : nothing}
        <div part="top-wrapper" class="cat-c-header__top">
          <div part="top-inner" class="cat-c-header__top-inner">
            <slot part="part-top" name="top"></slot>
          </div>
        </div>
        <div part="middle-wrapper" class="cat-c-header__middle">
          <div part="middle-container" class="cat-c-header__middle-container">
            <div part="middle-inner" class="cat-c-header__middle-inner">
              <slot part="part-default"></slot>
            </div>
          </div>
        </div>
        <div part="bottom-wrapper" class="cat-c-header__bottom">
          <slot part="part-bottom" name="bottom"></slot>
        </div>
      </header>
    `;
  }
}

if (customElements.get('cat-header') === undefined) {
  customElements.define('cat-header', BlocksHeader);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-header': BlocksHeader;
  }
}
